import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import { NearMe } from "@mui/icons-material";
import LocationCard from "../pages/location/locationCard";
import type { Location } from "../pages/location/locationCard";
import { GeoPoint } from "firebase/firestore";
import { getNearbyOfferLocations } from "../pages/offers/offersFirestore";

interface Props {
  storeId: string;
  acceptedLocationIds: string[];
  iconSize?: number;
}

const NearbyOfferLocationsButton: React.FC<Props> = ({
  storeId,
  acceptedLocationIds,
  iconSize = 40,
}) => {
  const [open, setOpen] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async () => {
    setOpen(true);
    setLoading(true);
    setError(null);

    try {
      const pos = await new Promise<GeolocationPosition>((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej, {
          enableHighAccuracy: true,
          timeout: 10_000,
        })
      );

      const found = await getNearbyOfferLocations(
        new GeoPoint(pos.coords.latitude, pos.coords.longitude),
        storeId,
        acceptedLocationIds,
        2 // km
      );
      setLocations(found);
    } catch (e: any) {
      setError(e.message ?? "Unable to load locations");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          width: iconSize,
          height: iconSize,
          backgroundColor: "white",
          borderRadius: "50%",
          boxShadow: 3,
          p: 1,
        }}
      >
        <NearMe color="primary" />
      </IconButton>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Nearby Locations (≤ 2 km)</DialogTitle>
        <DialogContent dividers>
          {loading && (
            <Typography align="center" sx={{ my: 2 }}>
              <CircularProgress size={24} />
            </Typography>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {!loading && !error && locations.length === 0 && (
            <Typography>No locations within 2 km.</Typography>
          )}

          {locations.map((loc) => (
            <LocationCard key={loc.locationId} location={loc} />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NearbyOfferLocationsButton;
